import { Badge, Header, SpaceBetween, Table } from '@amzn/awsui-components-console';
import React, { useState } from 'react';
import PAGES from '../../nav/Pages';
import { CustomStatusIndicator, RefreshButton, ToggleableDatetime } from '../helpers';
import { ActivityPriority, ActivityType } from '../../constants/hammerstoneConstants';
import { useGroupName, usePipelineCooldown } from 'src/data/redux';
import { useCollection } from '@amzn/awsui-collection-hooks';
import { msBetweenDates, useConditionalEffect, useTimeoutListRef, headerCounter } from 'src/commons';
import { compareBy } from 'src/commons/componentHelpers';
import { fetchActivityInfo, fetchPipelineActivitiesAndInstances } from 'src/data/api/fetchFromAPI';
import {
	LastExecutionDate,
	LastExecutionStatus,
	ActivityInfoWithStatus,
	useActivitiesWithStatus,
	useIsFetchingActivitiesWithStatus,
} from '../activity/LastActivityExecution';
import RerunExecutionInstanceButton from '../instance/RerunExecutionInstanceButton';
import PipelineBulkActionDropdown from './PipelineBulkActionDropdown';
import InternalLink from '../helpers/InternalLink';

// Due to a Polaris bug, all comparator functions must be declared OUTSIDE of a component in order to work
// Sage Post: https://refresh.sage.amazon.dev/posts/1371292#1371320
const lastExecutionDateComparator = compareBy<ActivityInfoWithStatus>('lastExecutionDate', msBetweenDates);
const updateDateComparator = compareBy<ActivityInfoWithStatus>('updateDate', msBetweenDates);

export default function PipelineActivitiesTable(props: { pipelineName: string; loading?: boolean }) {
	/** A ref to a list of timeout functions, which will be used to clean up remaining delay timeouts upon unmounting the component */
	const delayRefs = useTimeoutListRef();

	const groupName = useGroupName();
	const { pipelineName } = props;
	const activityList = useActivitiesWithStatus({ pipelineName, groupName });
	const fetchingActivitiesWithStatus = useIsFetchingActivitiesWithStatus({ pipelineName, groupName });
	const isCoolingDown = usePipelineCooldown(pipelineName);

	// Selection state
	const [selectedItems, setSelectedItems] = useState<ActivityInfoWithStatus[]>([]);

	const { items, collectionProps } = useCollection(activityList, {
		sorting: { defaultState: { sortingColumn: { sortingField: 'activityId' }, isDescending: false } },
		selection: {
			keepSelection: true,
			trackBy: 'activityId',
		},
	});

	useConditionalEffect(() => {
		fetchPipelineActivitiesAndInstances(pipelineName, groupName, { delays: delayRefs }, false);
	}, [pipelineName, groupName]);

	return (
		<Table
			{...collectionProps}
			items={items}
			loading={props.loading}
			selectionType="multi"
			selectedItems={selectedItems}
			onSelectionChange={({ detail }) => {
				setSelectedItems(detail.selectedItems);
			}}
			data-testid="pipelineActivitiesTable"
			header={
				<Header
					variant="h3"
					counter={headerCounter(selectedItems.length, items.length)}
					actions={
						<SpaceBetween size="xs" direction="horizontal">
							<RefreshButton
								refreshing={fetchingActivitiesWithStatus}
								disabled={isCoolingDown}
								onRefresh={() => {
									setSelectedItems([]);
									fetchPipelineActivitiesAndInstances(
										pipelineName,
										groupName,
										{ delays: delayRefs },
										true
									);
								}}
							>
								Status
							</RefreshButton>
							<PipelineBulkActionDropdown
								selectedActivities={selectedItems}
								setSelectedActivities={setSelectedItems}
								pipelineName={pipelineName}
								isRefreshing={fetchingActivitiesWithStatus}
								delayRefs={delayRefs}
							/>
						</SpaceBetween>
					}
				>
					Activities
				</Header>
			}
			columnDefinitions={[
				{
					id: 'activityId',
					header: 'ID',
					sortingField: 'activityId',
					cell: ({ activityId }) => (
						<InternalLink href={PAGES.VIEW_ACTIVITY.path} pathParams={{ activityId }} groupName>
							{activityId}
						</InternalLink>
					),
				},
				{
					id: 'activityName',
					header: 'Name',
					cell: (item) => item.activityName,
					sortingField: 'activityName',
				},
				{
					id: 'activityType',
					header: 'Type',
					cell: (item) => <Badge color="blue">{ActivityType[item.activityType]}</Badge>,
					sortingField: 'activityType',
				},
				{
					id: 'status',
					header: 'Activity status',
					cell: (item) => <CustomStatusIndicator>{item.status}</CustomStatusIndicator>,
					sortingField: 'status',
				},
				{
					id: 'lastExecutionStatus',
					header: 'Last execution status',
					cell: ({ activity, activityId }) => (
						<SpaceBetween size="xxs" direction="horizontal">
							<LastExecutionStatus activityId={activityId?.toString()} />{' '}
							{activity?.activityInstanceId && (
								<InternalLink
									groupName
									variant="secondary"
									href={PAGES.VIEW_INSTANCE.path}
									pathParams={{ activityId, instanceId: activity.activityInstanceId }}
								>
									(view)
								</InternalLink>
							)}
						</SpaceBetween>
					),
					sortingField: 'lastExecutionStatus',
				},
				{
					id: 'rerun',
					header: 'Rerun',
					cell: ({ activityId, activity }) => (
						<RerunExecutionInstanceButton
							activityId={activityId?.toString()}
							instanceId={activity?.activityInstanceId}
							variant="inline-icon"
							onSuccess={() => {
								fetchActivityInfo({ activityId, groupName }, true);
							}}
						/>
					),
				},
				{
					id: 'lastExecutionDate',
					header: 'Last execution date',
					// When there is no previous exeuction and when DJS throttles, the API will return nothing. In that case, we set the values to empty strings and display an 'Undetermined' message, as suggested by a customer (ecaglar@)
					cell: ({ lastExecutionDate, status }) => (
						<LastExecutionDate lastExecutionDate={lastExecutionDate} activityStatusCode={status} />
					),
					sortingComparator: lastExecutionDateComparator,
				},
				{
					id: 'priority',
					header: 'Priority',
					cell: (item) => ActivityPriority[item.activityPriority],
					sortingField: 'activityPriority',
				},
				{
					id: 'updateDate',
					header: 'Update date',
					cell: (item) => <ToggleableDatetime>{item.updateDate}</ToggleableDatetime>,
					sortingComparator: updateDateComparator,
				},
			]}
		/>
	);
}
